import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAdminAuth } from "../../context/AdminAuthContext";
import {
  LayoutDashboard, Users, CreditCard, Bell, Mail, Music,
  MonitorPlay, Settings, ScrollText, Shield, LogOut,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/admin/subscriptions", label: "Subscriptions", icon: CreditCard },
  { to: "/admin/notifications/push", label: "Push", icon: Bell },
  { to: "/admin/notifications/email", label: "Email", icon: Mail },
  { to: "/admin/music", label: "Music Library", icon: Music },
  { to: "/admin/ads", label: "Ads & Revenue", icon: MonitorPlay },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
  { to: "/admin/admins", label: "Admins", icon: Shield, role: "super_admin" },
];

export default function AdminLayout({ children, bgImage, bgOverlay }) {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const onLogout = () => { logout(); navigate("/admin/login", { replace: true }); };

  const bgUrl = bgImage || "/Admin%20Panel.webp";
  const overlay = bgOverlay != null ? bgOverlay : "rgba(0, 0, 0, 0.55)";

  return (
    <div className="min-h-screen flex relative" data-testid="admin-layout">
      {/* Background image (clover/sci-fi) */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${bgUrl})`,
          filter: "brightness(0.85) saturate(1.05)",
        }}
      />
      <div
        aria-hidden
        className="fixed inset-0 -z-10"
        style={{ background: overlay }}
      />
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-60 fixed inset-y-0 left-0 z-30"
        style={{ background: "rgba(6,6,6,0.78)", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", borderRight: "1px solid rgba(76,175,80,0.18)" }}
      >
        <div className="px-4 py-4 flex flex-col items-center border-b border-white/5">
          <img
            src="/care-for-flora-logo.png"
            alt="Care for Flora"
            width={140}
            style={{ height: "auto", filter: "drop-shadow(0 4px 14px rgba(76,175,80,0.45))" }}
            data-testid="admin-sidebar-logo"
          />
          <p className="text-[10px] uppercase tracking-wider text-muted-soft mt-2">Admin Console</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {NAV.filter((n) => !n.role || admin?.role === n.role).map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 my-0.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-[rgba(76,175,80,0.12)] text-white border border-[rgba(76,175,80,0.35)]"
                    : "text-white/55 hover:text-white hover:bg-white/3"
                }`
              }
              style={({ isActive }) => isActive ? { boxShadow: "0 0 12px rgba(76,175,80,0.18)" } : {}}
            >
              <Icon size={16} strokeWidth={1.6} />
              <span className="font-medium">{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-3 border-t border-white/5">
          <div className="px-2 py-2 mb-2">
            <p className="text-sm font-semibold truncate" data-testid="admin-user-name">{admin?.name}</p>
            <p className="text-[11px] text-muted-soft truncate">{admin?.email}</p>
            <span className="glass-pill mt-2 text-[10px]">{admin?.role}</span>
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/60 hover:text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-60 min-h-screen">
        <div className="px-5 sm:px-8 py-6 max-w-7xl mx-auto">{children}</div>
      </main>
    </div>
  );
}
